import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, Star, Clock, MapPin, ArrowRight, Filter, Grid, List } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useServices, useCategories } from '../hooks/useFirestoreServices';
import { normalizeCategory, isServiceActive } from '../utils/categoryNormalizer';
import { generateReviews, getReviewStats } from '../utils/reviewGenerator';
import LoadingSpinner from '../components/LoadingSpinner';

export default function ServicesPage() {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [viewMode, setViewMode] = useState('grid');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 50000 });
  const [quickFilters, setQuickFilters] = useState({
    popular: false,
    discount: false,
    rating: false,
    quick: false
  });
  
  // Infinite scroll state
  const [displayedCount, setDisplayedCount] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Fetch services and categories
  const { services: allServices, loading: servicesLoading } = useServices();
  const { categories: allCategories, loading: categoriesLoading } = useCategories();
  const loading = servicesLoading || categoriesLoading;

  // Filter active services AND exclude services with undefined/null categoryId
  const activeServices = allServices.filter(service => 
    isServiceActive(service) && service.categoryId && service.categoryId !== 'undefined'
  );

  // Debug: Show what we got from Firebase
  useEffect(() => {
    // Data validation only - no logging in production
  }, [allServices, activeServices, allCategories, loading]);

  // Get count for a category (case-insensitive match)
  const getCategoryCount = (categoryId: string) => {
    if (categoryId === 'all') return activeServices.length;
    
    return activeServices.filter(service => 
      service.categoryId?.toLowerCase() === categoryId.toLowerCase()
    ).length;
  };

  // Build categories with counts (show ALL categories)
  const categoriesWithCounts = [
    { id: 'all', name: 'All Services', icon: '🏠', count: activeServices.length },
    ...allCategories.map(cat => ({
      id: cat.id,
      name: cat.name,
      icon: cat.icon,
      count: getCategoryCount(cat.id)
    }))
  ];

  // Handle URL parameters
  useEffect(() => {
    const searchQuery = searchParams.get('search');
    if (searchQuery) setSearchTerm(searchQuery);
    
    const categoryParam = searchParams.get('category');
    if (categoryParam) setSelectedCategory(categoryParam);
  }, [searchParams]);

  // Debug selected category
  useEffect(() => {
    if (selectedCategory && selectedCategory !== 'all') {
      console.log('\n🔍 FILTERING BY CATEGORY:');
      console.log('Selected category:', selectedCategory);
      
      const matching = activeServices.filter(s => 
        s.categoryId?.toLowerCase() === selectedCategory.toLowerCase()
      );
      
      console.log(`Found ${matching.length} services:`);
      matching.forEach(s => {
        console.log(`  - ${s.name} (categoryId: "${s.categoryId}")`);
      });
      
      if (matching.length === 0) {
        console.log('❌ No matches! Check if category ID matches:');
        console.log('Available category IDs:', [...new Set(activeServices.map(s => s.categoryId))]);
      }
      console.log('\n');
    }
  }, [selectedCategory, activeServices]);

  // Filter services by selected category and other criteria
  const filteredServices = activeServices.filter(service => {
    // Search filter
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (service.provider || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Category filter - case-insensitive match
    const matchesCategory = selectedCategory === 'all' || 
      service.categoryId?.toLowerCase() === selectedCategory.toLowerCase();
    
    // Price filter
    const matchesPrice = service.price >= priceRange.min && service.price <= priceRange.max;
    
    // Quick filters logic
    const matchesQuickFilters = 
      (!quickFilters.popular || service.popular) &&
      (!quickFilters.discount || service.discount) &&
      (!quickFilters.rating || service.rating >= 4.5) &&
      (!quickFilters.quick || service.duration.includes('min') && parseInt(service.duration) < 60);
    
    return matchesSearch && matchesCategory && matchesPrice && matchesQuickFilters;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'price-low': return a.price - b.price;
      case 'price-high': return b.price - a.price;
      case 'rating': return b.rating - a.rating;
      case 'popular': return (b.popular ? 1 : 0) - (a.popular ? 1 : 0);
      default: return (b.reviewCount || b.reviews || 0) - (a.reviewCount || a.reviews || 0);
    }
  });

  // Debug final filtered results
  useEffect(() => {
    // Filter validation - removed for security
  }, [filteredServices, selectedCategory, priceRange, quickFilters, activeServices]);

  // Reset displayed count when filters change
  useEffect(() => {
    setDisplayedCount(10);
  }, [searchTerm, selectedCategory, sortBy, priceRange, quickFilters]);

  // Get services to display (limited by displayedCount)
  const displayedServices = filteredServices.slice(0, displayedCount);
  const hasMore = displayedCount < filteredServices.length;

  // Infinite scroll observer
  const loadMoreServices = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    // Simulate loading delay
    setTimeout(() => {
      setDisplayedCount(prev => Math.min(prev + 10, filteredServices.length));
      setIsLoadingMore(false);
    }, 500);
  }, [isLoadingMore, hasMore, filteredServices.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMoreServices();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoadingMore, loadMoreServices]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-primary-100 bg-clip-text text-transparent">
              All Services
            </h1>
            <p className="hidden md:block text-xl md:text-2xl text-primary-100 max-w-3xl mx-auto mb-8">
              Discover our complete range of professional home services delivered to your doorstep
            </p>
            <div className="hidden md:flex flex-wrap justify-center items-center gap-8 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span>{allServices.filter(s => s.isActive !== false).length}+ Services</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                <span>Expert Professionals</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                <span>Same Day Service</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-pink-400 rounded-full animate-pulse"></div>
                <span>Satisfaction Guaranteed</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
          
          {/* Left Side - Filters */}
          <div className="lg:w-80">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl md:rounded-2xl shadow-sm p-4 md:p-6 lg:sticky lg:top-8"
            >
              <div className="flex items-center space-x-2 mb-4 md:mb-6">
                <Filter className="h-4 w-4 md:h-5 md:w-5 text-primary-600" />
                <h3 className="text-base md:text-lg font-semibold text-gray-900">Filters</h3>
              </div>

              {/* Categories */}
              <div className="mb-4 md:mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 md:mb-3 text-sm md:text-base">
                  Categories ({categoriesWithCounts.length})
                </h4>
                <div className="space-y-1.5 md:space-y-2 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {categoriesWithCounts.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center justify-between p-2.5 md:p-3 rounded-lg text-left transition-colors ${
                        selectedCategory === category.id 
                          ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2 md:space-x-3">
                        <span className="text-lg md:text-xl">{category.icon}</span>
                        <span className="font-medium text-sm md:text-base">{category.name}</span>
                      </div>
                      <span className="text-xs md:text-sm text-gray-500">({category.count})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-4 md:mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 md:mb-3 text-sm md:text-base">Price Range</h4>
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className="relative flex-1">
                      <span className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm md:text-base">₹</span>
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange.min || ''}
                        onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value ? Number(e.target.value) : 0 })}
                        className="w-full pl-6 md:pl-7 pr-2 md:pr-3 py-2 md:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-800 font-medium shadow-sm hover:border-primary-300 transition-colors text-sm md:text-base"
                      />
                    </div>
                    <span className="text-gray-400 font-medium text-sm">—</span>
                    <div className="relative flex-1">
                      <span className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm md:text-base">₹</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange.max || ''}
                        onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value ? Number(e.target.value) : 2000 })}
                        className="w-full pl-6 md:pl-7 pr-2 md:pr-3 py-2 md:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-800 font-medium shadow-sm hover:border-primary-300 transition-colors text-sm md:text-base"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 md:gap-2">
                    {[
                      { label: 'Under ₹500', min: 0, max: 500 },
                      { label: '₹500-₹1000', min: 500, max: 1000 },
                      { label: '₹1000+', min: 1000, max: 2000 }
                    ].map(range => (
                      <button
                        key={range.label}
                        onClick={() => setPriceRange({ min: range.min, max: range.max })}
                        className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium border-2 rounded-lg transition-all shadow-sm ${
                          priceRange.min === range.min && priceRange.max === range.max
                            ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-md'
                            : 'border-gray-200 bg-white hover:border-primary-300 hover:text-primary-600 hover:shadow-md'
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                  <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg px-3 md:px-4 py-2 md:py-2.5 border border-primary-100">
                    <div className="text-xs md:text-sm font-medium text-primary-700">
                      Selected: <span className="font-bold">₹{priceRange.min}</span> - <span className="font-bold">₹{priceRange.max}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sort By */}
              <div className="mb-4 md:mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 md:mb-3 text-sm md:text-base">Sort By</h4>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-800 font-medium appearance-none cursor-pointer shadow-sm hover:border-primary-300 transition-colors text-sm md:text-base"
                  >
                    <option value="popular">Most Popular</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                    <option value="reviews">Most Reviewed</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 md:px-3 text-gray-700">
                    <svg className="fill-current h-4 w-4 md:h-5 md:w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Quick Filters */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Quick Filters</h4>
                <div className="space-y-2">
                  {[
                    { label: 'Popular Services', key: 'popular' as keyof typeof quickFilters },
                    { label: 'Discounted', key: 'discount' as keyof typeof quickFilters },
                    { label: 'Highly Rated (4.5+)', key: 'rating' as keyof typeof quickFilters },
                    { label: 'Quick Service (<1hr)', key: 'quick' as keyof typeof quickFilters }
                  ].map(filter => (
                    <label key={filter.key} className="flex items-center space-x-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={quickFilters[filter.key]}
                        onChange={(e) => setQuickFilters(prev => ({
                          ...prev,
                          [filter.key]: e.target.checked
                        }))}
                        className="rounded text-primary-600 focus:ring-primary-500" 
                      />
                      <span className="text-sm text-gray-700">{filter.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setPriceRange({ min: 0, max: 2000 });
                  setSortBy('popular');
                  setSearchTerm('');
                  setQuickFilters({
                    popular: false,
                    discount: false,
                    rating: false,
                    quick: false
                  });
                }}
                className="w-full mt-6 py-2 text-sm text-gray-600 hover:text-primary-600 transition-colors"
              >
                Clear All Filters
              </button>
            </motion.div>
          </div>

          {/* Right Side - Services Grid */}
          <div className="flex-1 lg:pl-8">
            {/* Results Header */}
            <div className="hidden md:flex justify-between items-center mb-4 md:mb-6">
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-900">Available Services</h2>
                <p className="text-xs md:text-base text-gray-600 mt-0.5 md:mt-1">
                  {(() => {
                    const categoryName = selectedCategory !== 'all' 
                      ? categoriesWithCounts.find(c => c.id === selectedCategory)?.name 
                      : '';
                    
                    if (loading || servicesLoading) {
                      return 'Loading services...';
                    }
                    
                    if (filteredServices.length === 0) {
                      return 'No services found';
                    }
                    
                    // Just show "Services loaded" or category name
                    if (categoryName) {
                      return `${categoryName} services`;
                    }
                    
                    return 'Services loaded';
                  })()}
                </p>
              </div>
              <div className="flex items-center space-x-1 md:space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 md:p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Grid className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 md:p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <List className="h-4 w-4 md:h-5 md:w-5" />
                </button>
              </div>
            </div>

            {/* Services Grid/List */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={viewMode === 'grid' 
                ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-3 md:gap-6"
                : "space-y-3 md:space-y-4"
              }
            >
              {displayedServices.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.05, 0.5) }}
                  className={`bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-200 transition-all duration-300 hover:-translate-y-1 overflow-hidden group ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                >
                  <div className={viewMode === 'list' ? 'w-32 md:w-40 flex-shrink-0' : ''}>
                    <div className="relative bg-gray-50 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.name}
                        className={`object-cover group-hover:scale-105 transition-transform duration-500 ${
                          viewMode === 'list' ? 'w-full h-32 md:h-full' : 'w-full h-44 md:h-48'
                        }`}
                      />
                      
                      {/* Compact Badges */}
                      <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
                        <div className="flex flex-col gap-1.5">
                          {service.popular && (
                            <span className="bg-yellow-500 text-white px-2.5 py-1 rounded-md text-xs font-bold shadow-md">
                              Popular
                            </span>
                          )}
                          {service.discount && (
                            <span className="bg-red-500 text-white px-2.5 py-1 rounded-md text-xs font-bold shadow-md">
                              {service.discount}% OFF
                            </span>
                          )}
                        </div>
                        {/* Category Badge - Prominent Styling */}
                        {service.category && (
                          <span className="bg-white/95 backdrop-blur-sm text-primary-700 text-xs font-bold px-3 py-1 rounded-md shadow-md border border-primary-200">
                            {service.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 md:p-5 flex-1 flex flex-col">
                    {/* Title and Price Row */}
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 pr-3">
                        <h3 className="text-base md:text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors leading-tight line-clamp-2 mb-1.5">
                          {service.name}
                        </h3>
                        {/* Category Badge below title */}
                        {service.category && (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
                            {service.category}
                          </span>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg md:text-xl font-black text-gray-900">₹{service.price.toLocaleString()}</div>
                        {service.originalPrice && (
                          <div className="text-gray-400 line-through text-xs">₹{service.originalPrice.toLocaleString()}</div>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-xs md:text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">{service.description}</p>

                    {/* Compact Stats Row */}
                    <div className="flex items-center gap-3 mb-3 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 text-yellow-400 fill-current" />
                        {(() => {
                          const reviews = generateReviews(service.id);
                          const stats = getReviewStats(reviews);
                          return (
                            <>
                              <span className="font-semibold text-gray-900">{stats.avgRating.toFixed(1)}</span>
                              <span className="text-gray-500">({stats.totalReviews})</span>
                            </>
                          );
                        })()}
                      </div>
                      <div className="h-3 w-px bg-gray-300"></div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{service.duration}</span>
                      </div>
                      {service.location && (
                        <>
                          <div className="h-3 w-px bg-gray-300 hidden md:block"></div>
                          <div className="flex items-center gap-1 hidden md:flex">
                            <MapPin className="h-3.5 w-3.5" />
                            <span className="truncate max-w-[120px]">{service.location}</span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Savings Badge */}
                    {service.originalPrice && service.originalPrice > service.price && (
                      <div className="mb-3">
                        <span className="inline-flex items-center text-xs font-bold text-green-600">
                          Save ₹{(service.originalPrice - service.price).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {/* CTA Button */}
                    <Link
                      to={`/services/${service.id}`}
                      className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2.5 px-4 rounded-lg font-semibold text-center text-sm transition-colors mt-auto flex items-center justify-center gap-1.5"
                    >
                      Book Now
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Loading More Indicator */}
            {hasMore && (
              <div ref={observerTarget} className="flex justify-center py-8">
                {isLoadingMore && (
                  <div className="flex items-center space-x-2 text-primary-600">
                    <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm font-medium">Loading more services...</span>
                  </div>
                )}
              </div>
            )}

            {filteredServices.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No services found</h3>
                <p className="text-gray-600">Try adjusting your search or filter criteria</p>
              </motion.div>
            )}
          </div>


        </div>
      </div>
    </div>
  );
}
